import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BETA_PATH } from '../content';

/**
 * The closed-beta dialog shown when someone chooses Register.
 *
 * Registration is gated: most people need to apply first, but anyone holding a
 * registration code should not be sent through the application queue. So this
 * explains the gate and offers both doors rather than guessing which one the
 * visitor needs.
 *
 * Built on the native `<dialog>` element deliberately. `showModal()` brings
 * focus trapping, Escape-to-close, inertness of the page behind, and top-layer
 * stacking — all things a div-based modal has to reimplement, usually
 * incompletely. The only things left to add are backdrop-click and returning
 * focus to whatever opened it.
 *
 * Renders closed on the server, so it costs the prerendered snapshot nothing
 * but its markup.
 */

interface RegisterDialogProps {
  open: boolean;
  onClose: () => void;
}

export function RegisterDialog({ open, onClose }: RegisterDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  /** Element that had focus before opening, so it can be restored on close. */
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      openerRef.current = document.activeElement;
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // `close` fires for Escape and for programmatic close alike, so restoring
  // focus here covers every exit path rather than just the button.
  const handleClose = () => {
    const opener = openerRef.current;
    if (opener instanceof HTMLElement) opener.focus();
    onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      className="fias-dialog"
      aria-labelledby="fias-register-dialog-title"
      onClose={handleClose}
      // The dialog element fills the top layer, so a click landing on the
      // element itself (rather than the panel inside it) is a backdrop click.
      onClick={(event) => {
        if (event.target === dialogRef.current) onClose();
      }}
    >
      <div className="fias-dialog-panel">
        <p className="fias-eyebrow">
          Closed <span className="fias-eyebrow-trail">beta</span>
        </p>

        <h2
          id="fias-register-dialog-title"
          className="mt-4 font-sans text-2xl font-semibold tracking-[-0.02em] text-[color:var(--fias-bright)]"
        >
          Fias is currently in a closed beta
        </h2>

        <p className="mt-4 font-sans text-sm leading-relaxed text-[color:var(--fias-muted)]">
          You can{' '}
          <Link to={BETA_PATH} className="fias-dialog-inline-link">
            apply to join the beta here
          </Link>
          , or continue to register if you already have a registration code.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/auth/signup" className="fias-btn fias-btn-pill fias-btn-light">
            Continue to register
          </Link>
          <button type="button" onClick={onClose} className="fias-btn fias-btn-pill fias-btn-veil">
            Cancel
          </button>
        </div>
      </div>
    </dialog>
  );
}
